import { blinkConstants } from '@thoughtspot/blink-constants';
import { NavService, PrivilegedRouteObject } from '@thoughtspot/blink-context';
import { jsonConstants } from '@thoughtspot/json-constants';
import { userActionTypeEnum } from '@thoughtspot/metrics';
import {
    getCurrentOrgId,
    getCurrentOrgName,
    getDemoWorsheetGuids,
    hasAdminPrivileges,
    hasDeveloperPrivileges,
    hasPreviewThoughtspotSagePrivileges,
    isAdminRoutingEnabled,
    isOrgPerUrlEnabled,
    isOrgUiEnabled,
    isPerOrgSubDomainEnabled,
    isRedirectToV1Disabled,
    v1Disabled,
} from '@thoughtspot/session-service';
import {
    disableSAMLAutoRedirect,
    getIdpDomain,
    isDeepLinkingDisabled,
} from '@thoughtspot/system-config-service';
import _ from 'lodash';
import { useState } from 'react';
import {
    matchPath,
    NavigateFunction,
    NavigateOptions,
    To,
    useMatches,
} from 'react-router';
import {
    generatePath,
    unstable_usePrompt as usePrompt,
    useNavigate as useRouterNavigate,
    useSearchParams,
} from 'react-router-dom';
import { Subject, Subscription } from 'rxjs';
import { useAppContext } from '/@contexts/global-app-context';
import { ADMIN_RESET_EVENT } from '/@utils/admin-events';
import {
    isEmbeddedApp,
    isLiveboardEmbed,
    isSageEmbed,
    shouldOverrideHistoryStateInEmbed,
} from '/@utils/embed.util';
import { markEvent } from '/@utils/pinboard-perf.util';
import { CORE_ROUTES } from '../../../routing/core-routes';
import { ApplicationList, Applications, ROUTES } from '../../../routing/routes';
import {
    getFragmentFeedbackPath,
    getQueryFeedbackPath,
    routePaths,
} from '../../../routing/routes-util';
import {
    FLAGS,
    flags,
    getQueryParamsAndQueryString,
} from '../config-service/flags-service';

export interface QueryParams {
    name: string;
    value: string | boolean | number;
}

const ABSOLUTE_URL_PATTERN = '{protocol}//{host}/{urlParams}#{relativePath}';
const adminTabObserver = new Subject<string>();
let adminTabObserverSubscription: Subscription | undefined;
const QUERY_PARAM_DELIMITER = '#';

export function addClientQueryParamsToPath(
    path: string,
    flags: Array<QueryParams>,
) {
    if (!flags) {
        return path;
    }
    let newPath = path;
    newPath = `${newPath}?`;
    flags.forEach((flag, index) => {
        if (index !== 0) {
            newPath = `${newPath}&`;
        }
        newPath = `${newPath}${flag.name}=${flag.value}`;
    });
    return newPath;
}

function getUrlHref(): string {
    return window.location.href;
}

export const getUrlHash = () => {
    return window.location.hash;
};

function getEmbedRoute(route: string, isEmbedded: boolean): string {
    return isEmbedded ? `Embed_${route}` : route;
}

/*
 * isPlatformDeeplinkSupported checks whether platform is Android or IOS
 */
function isPlatformDeeplinkSupported() {
    const toMatch = [/Android/i, /iPhone/i, /iPad/i, /iPod/i];
    /* Added platform and maxTouchPoints check because starting iPadOs userAgent
     * that comes in safari doesn't mention that it is an iPad as Apple wants iPad to request desktop site by default
     */
    return (
        (navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints &&
            navigator.maxTouchPoints > 1) ||
        toMatch.some(toMatchItem => navigator.userAgent.match(toMatchItem))
    );
}

/*
 * doesUserWantBrowserExperience tell whether user has pressed on Continue in browser button
 */
function doesUserWantBrowserExperience() {
    // sessionStorage.getItem only return string
    return (
        sessionStorage.getItem(
            blinkConstants.deeplink.userWishesBrowserExperience,
        ) === 'true'
    );
}

const edgeOnboarding = (urlQueryParams?: Array<QueryParams>) => {
    const workSheetGuid = getDemoWorsheetGuids()[0];
    const internalDataSourceId = urlQueryParams?.find(
        item => item.name === 'internalDataSourceId',
    );
    const dataSourceId = urlQueryParams?.find(
        item => item.name === 'dataSourceId',
    );
    let dataSourceIdValue;
    if (dataSourceId) dataSourceIdValue = dataSourceId.value;
    else if (internalDataSourceId)
        dataSourceIdValue = internalDataSourceId.value;
    else dataSourceIdValue = workSheetGuid;
    const currentStepValue = urlQueryParams?.find(
        item => item.name === 'currentStep',
    );
    let externalDataAppend;
    if (dataSourceId) {
        externalDataAppend = `?externalDatasourceConnected=${!_.isNil(
            dataSourceId,
        )}`;
    } else if (currentStepValue) {
        externalDataAppend = `?currentStep=${currentStepValue.value}`;
    } else externalDataAppend = '';
    const path = generatePath(
        `/edge/onboarding/${dataSourceIdValue}${externalDataAppend}`,
    );
    return path;
};

const getAbsoluteUrl = (relativePath: string) => {
    const { queryString } = getQueryParamsAndQueryString();
    return ABSOLUTE_URL_PATTERN.replace('{protocol}', window.location.protocol)
        .replace('{host}', window.location.host)
        .replace('{urlParams}', queryString)
        .replace('{relativePath}', relativePath);
};

export function getHost(): string {
    const { queryString } = getQueryParamsAndQueryString();
    return `${window.location.origin}${queryString}`;
}

// TODO: Sreeni to take look at this for long term usage.
export function getSubDomainIfOrgEnabled(): string {
    if (!isOrgUiEnabled()) {
        return '';
    }
    const url = new URL(getUrlHref());
    const domains = url.hostname.split('.');
    if (domains[1] === getIdpDomain()) {
        return `${domains[0]}_`;
    }
    return '';
}

const emptyNavigate = () => {
    throw new Error('Nav service not initialized yet!');
};

const triggerAdminTabSwitch = (adminTabId: string) => {
    adminTabObserver.next(adminTabId);
};

const unsubscribeAdminTabObserver = () => {
    if (adminTabObserverSubscription) {
        adminTabObserverSubscription.unsubscribe();
        adminTabObserverSubscription = undefined;
    }
};

const adminTabObserverSubscriber = (cb: (landingTabId: string) => void) => {
    unsubscribeAdminTabObserver();
    adminTabObserverSubscription = adminTabObserver.subscribe(
        (landingTabId: string) => {
            cb(landingTabId);
        },
    );
};

function isCurrentAdminPath() {
    const hash = window.location.hash;
    return hash === '#/admin' || hash.startsWith('#/admin/');
}

// sessionStorage key holding the admin sub-tab a TML import was launched from,
// so the import screen's exit can return to it. Written here in blink-v2 (see
// captureAdminImportOrigin) rather than the admin MFE's redirectToImport, so
// the capture does not depend on that separately-built bundle being refreshed.
// Read and cleared by the exit handler in import-tsl-v2.tsx, which imports this
// same constant so the two stay in lockstep (SCAL-317297). sessionStorage is
// per-tab and survives the same-tab reload of the cross-bundle hand-off, so it
// avoids the cross-tab contamination and force-close staleness of localStorage.
export const ADMIN_IMPORT_RETURN_PATH_KEY = 'adminImportReturnPath';

// sessionStorage key holding the path a MULTIPLE_TYPES TML import was launched
// from, so the import screen's exit can return to it. Written here alongside
// ADMIN_IMPORT_RETURN_PATH_KEY (see captureMultipleTypesImportOrigin) so both
// captures live in one place. Read and cleared by the exit handler in
// import-tsl-v2.tsx, which imports this constant so the two stay in lockstep
// (SCAL-324600). sessionStorage is per-tab and avoids the cross-tab
// contamination and force-close staleness of localStorage.
export const MULTIPLE_TYPES_IMPORT_RETURN_PATH_KEY =
    'multipleTypesImportReturnPath';
// Literal value of the MULTIPLE_TYPES route param (defined in app-template.util)
// used here to avoid a cross-boundary import into the nav-service.
const MULTIPLE_TYPES_STR = 'MULTIPLE_TYPES';

// When navigating to the TML import screen from an admin sub-tab, stash that
// sub-path (everything after `#/admin/`) so exit can return to exactly that
// tab. The hash still reflects the admin page at call time. Captured verbatim
// so the build-specific slug format (SaaS `user-management/roles` vs software
// `user-management-roles`) and the all-orgs prefix round-trip without either
// app knowing the other's URL scheme. Always set-or-clear so a stale value
// can't leak into a later import from a non-admin surface (SCAL-317297).
function captureAdminImportOrigin(path: string) {
    if (!path || !path.includes('/import-tsl/')) return;
    const match = (window.location.hash || '').match(/^#\/admin\/(.+)$/);
    if (match) {
        sessionStorage.setItem(ADMIN_IMPORT_RETURN_PATH_KEY, match[1]);
    } else {
        sessionStorage.removeItem(ADMIN_IMPORT_RETURN_PATH_KEY);
    }
}

// When navigating to the MULTIPLE_TYPES TML import screen, stash the full
// current hash so exit can navigate back directly. This is needed because when
// overrideHistoryState is enabled the SDK converts every pushState to a
// replaceState, so history.back() exits the embed and lands on the host app's
// previous page instead of staying inside ThoughtSpot (SCAL-324600).
// Always set-or-clear so a stale value cannot leak into a later import from a
// different surface.
function captureMultipleTypesImportOrigin(path: string) {
    if (!path || !path.includes('/import-tsl/')) return;
    if (path.includes(`/import-tsl/${MULTIPLE_TYPES_STR}`)) {
        sessionStorage.setItem(
            MULTIPLE_TYPES_IMPORT_RETURN_PATH_KEY,
            window.location.hash,
        );
    } else {
        sessionStorage.removeItem(MULTIPLE_TYPES_IMPORT_RETURN_PATH_KEY);
    }
}

export function getNavService(
    navigate: NavigateFunction = emptyNavigate,
    setNavPrompt?: (navPromptProps: { message: string; when: boolean }) => void,
    getLast401Path?: () => string,
): NavService {
    return {
        goToDataModelPage: () => {
            const path = generatePath(ROUTES.DataModelPage.path);
            navigate(path);
        },
        goToAdminPage: (subPath?: string) => {
            // Deep-link back to a specific admin sub-tab (e.g. exiting the
            // import screen returns to the originating Roles/Users/Groups tab
            // instead of resetting to the first-available tab — SCAL-317297).
            // The URL is authoritative here, so we must NOT dispatch
            // ADMIN_RESET_EVENT, which would override the target sub-tab with
            // the sidebar's first-available tab.
            if (isAdminRoutingEnabled() && subPath) {
                // Keep the target inside the /admin subtree: strip leading
                // slashes and drop any `..` traversal segments before building
                // the URL.
                const safePath = subPath
                    .split('/')
                    .filter(segment => segment && segment !== '..')
                    .join('/');
                navigate(`/admin/${safePath}`);
                return;
            }
            navigate(generatePath(ROUTES.AdminPage.path));
            if (isAdminRoutingEnabled()) {
                // The URL is authoritative; the sidebar resolves first-available
                // on reset.
                window.dispatchEvent(new CustomEvent(ADMIN_RESET_EVENT));
            }
        },
        getCurrentPathByLocationHash: (includeQueryParams: boolean) => {
            let currentPath = window.location.hash;
            if (currentPath.startsWith('#')) {
                currentPath = currentPath.slice(1);
            }
            if (includeQueryParams) return currentPath;
            return currentPath.split('?')[0];
        },
        goToHome: () => {
            const path = generatePath(ROUTES.StratusHome.path);
            navigate(path);
        },
        goToCollections: () => {
            const path = generatePath(ROUTES.Collections.path);
            navigate(path);
        },
        goToCollection: (collectionId: string) => {
            const path = generatePath(ROUTES.Collections.path, {
                collectionId,
            });
            navigate(path);
        },
        goToAnswers: () => {
            const path = generatePath(ROUTES.Answers.path);
            navigate(path);
        },
        goToStratusHome: (urlQueryParams?: Array<QueryParams>) => {
            let path = generatePath(ROUTES.StratusHome.path);
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToCopilot: (
            dataSourceId?: string,
            urlQueryParams?: Array<QueryParams>,
        ) => {
            let path = generatePath(ROUTES.Copilot.path);
            if (dataSourceId) {
                path = `${path}/${dataSourceId}`;
            }
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToCopilotChat: (
            conversationId: string,
            urlQueryParams?: Array<QueryParams>,
        ) => {
            let path = generatePath(ROUTES.Copilot.path);
            path = `${path}/chat/${conversationId}`;
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToAskSageChat: (
            urlQueryParams: Array<QueryParams>,
            isEmbedded?: boolean,
            currentLocation?: string,
            inNewTab?: boolean,
        ) => {
            let path = generatePath(
                ROUTES[getEmbedRoute('ConvAssist', isEmbedded)].path,
            );
            if (urlQueryParams?.length) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            if (inNewTab) {
                const newWindow = window.open(
                    `${getHost()}/#${path}`,
                    '_blank',
                );
                return newWindow !== null;
            }
            const shouldReplace = currentLocation === path;
            navigate(path, { replace: shouldReplace });
            return true;
        },
        goToUrl: (url: string, inNewTab?: boolean) => {
            if (inNewTab) {
                window.open(`${getHost()}/#${url}`, '_blank');
            } else {
                navigate(url);
            }
        },
        goToPath: (
            path: string,
            options?: {
                replace?: boolean;
                state?: Record<string, any>;
                queryParams?: Array<QueryParams>;
                forceFullPageLoad?: boolean;
            },
        ) => {
            const { replace, state, queryParams, forceFullPageLoad } =
                options || {};
            // Remember the originating admin sub-tab before leaving for the
            // import screen (SCAL-317297). Runs for every navigation mode.
            captureAdminImportOrigin(path);
            captureMultipleTypesImportOrigin(path);
            if (forceFullPageLoad) {
                if (shouldOverrideHistoryStateInEmbed()) {
                    window.location.replace(path);
                } else {
                    window.location.href = path;
                }
                return;
            }
            const currentHashPath = window.location.hash
                .replace('#', '')
                .split('?')[0];
            const cleanPath = _.last(path?.split('#'));
            const targetPath = cleanPath || currentHashPath;
            const resolvedPath = queryParams?.length
                ? addClientQueryParamsToPath(targetPath, queryParams)
                : targetPath;
            navigate(resolvedPath, _.omitBy({ replace, state }, _.isUndefined));
        },
        goToEdgeOnboarding: (
            urlQueryParams?: Array<QueryParams>,
            isRevisiting = false,
        ) => {
            const path = edgeOnboarding(urlQueryParams);
            if (isRevisiting) navigate(`${path}?isRevisiting=true`);
            else navigate(path);
        },
        goToConnectOwnData: () => {
            const path = generatePath(routePaths.ConnectOwnData);
            navigate(path);
        },
        goToSparkflowOnboarding: () => {
            const path = generatePath(ROUTES.Sparkflow.path);
            navigate(path);
        },
        goToEurekaWithQueryParams: (urlQueryParams: Array<QueryParams>) => {
            let path = generatePath(
                ROUTES[getEmbedRoute('EurekaPage', false)].path,
            );
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToMonitorV2: () => {
            const path = generatePath(ROUTES.Monitor.path);
            navigate(path);
        },
        goToSavedAnswer: (id: string, isEmbedded?: boolean) => {
            // SavedAnswer is a child route under SavedAnswerLayout; use the
            // layout path (minus the /* wildcard) for navigation.
            const rawPath = isEmbedded
                ? ROUTES.Embed_SavedAnswer.path
                : ROUTES.SavedAnswerLayout.path.replace('/*', '');
            const path = generatePath(rawPath, { answerId: id });
            navigate(path);
        },
        goToSavedAnswerWithClientFlags: (
            id: string,
            urlQueryParams?: Array<QueryParams>,
            isEmbedded?: boolean,
        ) => {
            const rawPath = isEmbedded
                ? ROUTES.Embed_SavedAnswer.path
                : ROUTES.SavedAnswerLayout.path.replace('/*', '');
            let path = generatePath(rawPath, { answerId: id });
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToView: (id: string, urlQueryParams?: Array<QueryParams>) => {
            let path = generatePath(ROUTES.View.path, {
                answerId: id,
            });
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        goToSavedSqlView: (id: string, urlQueryParams?: Array<QueryParams>) => {
            let path = generatePath(ROUTES.Tables.path, {
                sqlViewId: id,
            });
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            navigate(path);
        },
        getAbsoluteUrl: (path: string) => {
            return `${getHost()}/#${path}`;
        },
        goToEditACopy: (
            id: string,
            shouldOpenInNewTab: boolean,
            urlQueryParams?: Array<QueryParams>,
            isEmbedded?: boolean,
        ) => {
            let path = generatePath(
                ROUTES[getEmbedRoute('EditACopy', isEmbedded)].path,
                {
                    editACopySessionKey: id,
                },
            );
            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }
            if (shouldOpenInNewTab) {
                const editACopyPath = `${getHost()}#${path}`;
                window.open(editACopyPath, '_blank');
                return;
            }
            navigate(path);
        },
        goToPinboard: (
            pinboardId: string,
            tabId: string,
            vizId: string,
            urlQueryParams?: Array<QueryParams>,
            replaceState = false,
            navigationState?: Record<string, string>,
            preserveCurrentPath = false,
        ) => {
            markEvent(userActionTypeEnum.REDIRECTED_TO_PINBOARD);
            const pathname = window.location.hash;

            // For embedded liveboards, preserve the current path and only update query params.
            // This prevents navigation away from the embedded context (e.g., Data tab).
            if (preserveCurrentPath) {
                const currentPath = pathname.replace('#', '').split('?')[0];
                const newPath =
                    urlQueryParams?.length > 0
                        ? addClientQueryParamsToPath(
                              currentPath,
                              urlQueryParams,
                          )
                        : currentPath;
                navigate(newPath, { replace: true });
                return;
            }
            let path = '';
            if (isLiveboardEmbed()) {
                if (tabId) {
                    path = generatePath(
                        ROUTES[getEmbedRoute('TabEmbed', true)].path,
                        {
                            pinboardId,
                            tabId,
                            vizId,
                        },
                    );
                } else {
                    path = generatePath(
                        ROUTES[getEmbedRoute('PinboardEmbed', true)].path,
                        {
                            pinboardId,
                        },
                    );
                }
            } else if (tabId) {
                path = generatePath(ROUTES.Tab.path, {
                    pinboardId,
                    tabId,
                    vizId,
                });
            } else {
                path = generatePath(ROUTES.Pinboard.path, {
                    pinboardId,
                    vizId,
                });
            }

            if (urlQueryParams) {
                path = addClientQueryParamsToPath(path, urlQueryParams);
            }

            if (
                pathname.includes(
                    generatePath(routePaths.EdgeOnboarding, {
                        worksheetGuid: '',
                    }),
                )
            ) {
                path += '?edgeOnboarding=true';
                getNavService().goToUrl(path, true);
            } else {
                navigate(path, {
                    replace: replaceState && !isLiveboardEmbed(),
                    ...(navigationState ? { state: navigationState } : {}),
                });
            }
        },
        goToEmbraceOauthRedirection: () => {
            const path = generatePath(ROUTES.EmbraceOauth.path);
        },
        goToCustomCalendarRedirection: () => {
            const path = generatePath(ROUTES.CustomCalendarTest.path);
            navigate(path);
        },
        goToHomeAnswers: () => {
            navigate('/insights/home/answers');
        },
        goToTryEverywhere: () => {
            navigate('/everywhere');
        },
        goToHomeLiveboards: () => {
            const path = '/insights/home/liveboards';
            navigate(path);
        },
        goToHomeFavs: () => {
            navigate('/insights/home/favourites');
        },
        goToHomeLiveboardSchedules: () => {
            navigate('/insights/home/liveboard-schedules');
        },
        goToHomeCreatedByMe: () => {
            navigate('/insights/home/created-by-me');
        },
        goToHomeMonitorAlerts: () => {
            const path = '/insights/home/monitor-alerts';
            navigate(path);
        },
        goToHomeSpotIQAnalysis: () => {
            navigate('/insights/home/spotiq-analysis');
        },
        goToAnswer: (isEmbedded?: boolean) => {
            // AdhocAnswer is a child route under AnswerLayout; use the
            // layout path (minus the /* wildcard) for navigation.
            const rawPath = isEmbedded
                ? ROUTES.Embed_Answer.path
                : ROUTES.AnswerLayout.path.replace('/*', '');
            navigate(generatePath(rawPath));
        },
        goToRouteKey: (routeKey, id, search) => {
            const path = generatePath(ROUTES[routeKey].path, {
                id,
            });
            navigate(path);
        },
        onPrompt: (message: string, when: () => boolean) => {
            setNavPrompt({ message, when: when() });
        },
        deregisterOnPrompt: (componentName: string) => {
            setNavPrompt({ message: '', when: false });
        },
        readSearchParams: (): URLSearchParams => {
            const [searchParams] = useSearchParams();
            return searchParams;
        },
        readClientQueryParamsFromPath: (
            urlHash?: string,
        ): Map<string, string | boolean | number> => {
            const hash = urlHash || getUrlHash();
            const clientSideQueryParams = new Map<
                string,
                string | boolean | number
            >();
            const params = hash.split('?').pop();
            params.split('&').forEach(param => {
                const flag = param.split('=');
                if (flag.length === 2) {
                    clientSideQueryParams.set(flag[0], flag[1]);
                }
            });
            return clientSideQueryParams;
        },
        isCurrentSearchPath: () => {
            const pathname = window.location.hash;
            return pathname.includes(
                ROUTES.AnswerLayout.path.replace('/*', '').replace('?', ''),
            );
        },
        isCurrentEurekaPath: () => {
            const pathname = window.location.hash;
            // we get ROUTES.EurekaPage.path as '/insights?/eureka' and
            // pathname as '#/insights/eureka?query=' for eureka srp
            // and '#/embed/eureka?query=' for sage embed
            return (
                pathname.includes(ROUTES.EurekaPage.path.replace('?', '')) ||
                pathname.includes(
                    ROUTES[
                        getEmbedRoute('EurekaPage', isSageEmbed())
                    ].path.replace('insights?/', ''),
                ) // this is needed until we add navigation to eureka mfe
            );
        },
        isCurrentTrainSagePath: () => {
            const pathname = window.location.hash;
            return pathname.includes(ROUTES.TrainSagePage.path);
        },
        isCurrentHomePath: () => {
            const pathname = window.location.hash;
            return pathname.includes(ROUTES.Home.path);
        },
        isCurrentStratusPath: () => {
            const pathname = window.location.hash;
            return pathname.includes(ROUTES.StratusHome.path);
        },
        isCurrentPrintViewPath: () => {
            const pathname = window.location.hash;
            return (
                pathname.includes(ROUTES.PrintView.path) ||
                pathname.includes(ROUTES.PrintViewV2.path)
            );
        },
        isCurrentConvAssistPath: (isFromSparkflow?: boolean) => {
            const pathname = window.location.hash;
            // Validates if current URL is conv-assist route
            // Matches: /insights/conv-assist or /conv-assist or /embed/conv-assist
            const isConvAssist =
                pathname.includes(ROUTES.ConvAssist?.path.replace('?', '')) ||
                pathname.includes(
                    ROUTES[
                        getEmbedRoute('ConvAssist', isEmbeddedApp())
                    ]?.path.replace('insights?/', ''),
                );
            if (isFromSparkflow) {
                return (
                    isConvAssist && pathname.includes('isFromSparkflow=true')
                );
            }
            return isConvAssist;
        },
        goToCustomCalendarWizard: () => {
            const path = generatePath(ROUTES.CustomCalendar.path);
            navigate(path);
        },
        goToSetup: () => {
            const path = generatePath(ROUTES.SetupHub.path);
            navigate(path);
        },
        goToActions: () => {
            const path = generatePath(ROUTES.EverywherePage.path);
            navigate(`${path}/actionsCustomization`);
        },
        goToOnBoarding: () => {
            navigate('/onboarding?isRevisiting=true');
        },
        goToAutoAnswer: (dataSourceId: string, flags: Array<QueryParams>) => {
            const path = generatePath(ROUTES.AutoAnswer.path, {
                dataSourceId,
            });
            const newPath = addClientQueryParamsToPath(path, flags);
            navigate(newPath);
        },
        goToRequestAccessForObject: (
            objectType: string,
            objectId: string,
            flags: Array<QueryParams>,
        ) => {
            const path = generatePath(ROUTES.RequestAccess.path, {
                objectType,
                objectId,
            });
            const newPath = addClientQueryParamsToPath(path, flags);
            navigate(newPath);
        },
        goToUserPreference: () => {
            const path = generatePath(ROUTES.UserPreferencePage.path);
            navigate(path);
        },
        openExternalUrlInNewTab: (externalLinkPath: string) => {
            const externalLinkUrl = `${externalLinkPath}`;
            window.open(externalLinkUrl, '_blank');
        },
        isCurrentSavedAnswerPath: () => {
            return getUrlHref().indexOf('/saved-answer') > -1;
        },
        isCurrentPinboardPath: () => {
            return getUrlHref().indexOf('/pinboard') > -1;
        },
        isEmbedded: () => {
            return getUrlHref().indexOf('/embed/') > -1;
        },
        goToLogin: () => {
            const path = generatePath(ROUTES.Login.path);
            navigate(path);
        },
        goToInsights: (tabId: string) => {
            const path = generatePath(ROUTES.SpotIQList.path);
            navigate(path);
        },
        goToInsight: (analysisResultId: string) => {
            const path = generatePath(ROUTES.SpotIQ.path, {
                analysisResultId,
            });
            navigate(path);
        },
        goToInsightInNewTab: (analysisResultId: string) => {
            const path = generatePath(ROUTES.SpotIQ.path, {
                analysisResultId,
            });
            window.open(`${getHost()}/#${path}`, '_blank');
        },
        goToHomeInNewTab: (
            queryParams: Array<QueryParams>,
            setWindowName: boolean,
        ) => {
            const path = generatePath(ROUTES.StratusHome.path);
            const newPath = !_.isEmpty(queryParams)
                ? addClientQueryParamsToPath(path, queryParams)
                : path;
            if (setWindowName) {
                window.name = getUrlHref() + Date.now().toString();
            }
            window.open(`${getHost()}/#${newPath}`, '_blank');
        },
        goToTable: (id: string) => {
            const path = generatePath(`/data/tables/${id}`);
            navigate(path);
        },
        goToDataset: () => {
            const path = generatePath('/data/dataset');
            navigate(path);
        },
        goToDestinationSync: () => {
            const path = generatePath('/data/destination-sync');
            navigate(path);
        },
        goToSelectedDestinationSync: (id: string) => {
            const path = generatePath(`/data/destination-sync/${id}`);
            navigate(path);
        },
        goToUtilities: () => {
            const path = generatePath('/data/utilities');
            navigate(path);
        },
        goToDbt: () => {
            const path = generatePath('/data/dbt');
            navigate(path);
        },
        goToDestination: () => {
            const path = generatePath('/data/destination');
            navigate(path);
        },
        goToSqlView: () => {
            const path = generatePath('/data/sql-view');
            navigate(path);
        },
        goToSavedSqlViewPage: (id: string, flags: QueryParams[]) => {
            let path = generatePath('/data/sql-view');
            if (!_.isNil(id)) {
                path = generatePath('/data/sql-view/:id', {
                    id,
                });
            }
            navigate(path);
        },
        goToDataGovernance: () => {
            const path = generatePath('/data/data-governance');
            navigate(path);
        },
        goToLiveBoardVerification: () => {
            const path = generatePath('/data/liveboard-verification');
            navigate(path);
        },
        getFragmentFeedbackPath,
        getQueryFeedbackPath,
        goToFragmentFeedback: (extraParams?: Array<QueryParams>) => {
            const path = generatePath(getFragmentFeedbackPath(extraParams));
            navigate(path);
        },
        goToQueryFeedback: (extraParams?: Array<QueryParams>) => {
            const path = generatePath(getQueryFeedbackPath(extraParams));
            navigate(path);
        },
        goToAppConnections: () => {
            const path = generatePath('/data/app-connections');
            navigate(path);
        },
        goToDestinationType: (type: string) => {
            const path = generatePath(
                `/data/destination-sync/auth/type/${type}`,
            );
            navigate(path);
        },
        goToDestinationAuthComplete: () => {
            const path = generatePath('/data/destination-sync/auth/complete');
            navigate(path);
        },
        goToTeamsMembers: () => {
            const path = generatePath('/teams/members');
            navigate(path);
        },
        goToTeamsPendingInvitations: () => {
            const path = generatePath('/teams/pending-invitations');
            navigate(path);
        },
        goToTeamsPlanOptions: () => {
            const path = generatePath('/teams/plan-options');
            navigate(path);
        },
        goToTeamsSpotIQOptions: () => {
            const path = generatePath('/teams/spotiq');
            navigate(path);
        },
        goToTeamsStyleCustomizationOptions: () => {
            const path = generatePath('/teams/style-customization');
            navigate(path);
        },
        goToTeamsUserManagementOptions: () => {
            const path = generatePath('/teams/user-management');
            navigate(path);
        },
        goToTeamsAuthenticationOptions: () => {
            const path = generatePath('/teams/authentication');
            navigate(path);
        },
        goToTeamsSystemActivitiesOptions: () => {
            const path = generatePath('/teams/system-activities');
            navigate(path);
        },
        goToTeamsDataUsage: () => {
            const path = generatePath('/teams/data-usage');
            navigate(path);
        },
        goToTeamsManageSubscription: () => {
            const path = generatePath('/teams/manage-subscription');
            navigate(path);
        },
        goToManageSchedules: (pinboardId: string) => {
            const path = generatePath(`/schedules/${pinboardId}`);
            navigate(path);
        },
        goToOrgsHome: () => {
            const path = generatePath('/orgs');
            navigate(path);
        },
        goToOrgsUsers: () => {
            const path = generatePath('/orgs/users');
            navigate(path);
        },
        goToOrgsGroups: () => {
            const path = generatePath('/orgs/groups');
            navigate(path);
        },
        goToOrgsContent: () => {
            const path = generatePath('/orgs/content');
            navigate(path);
        },
        goToOrgsCreditConsumption: () => {
            const path = generatePath('/orgs/credit-consumption');
            navigate(path);
        },
        goToResetPassword: () => {
            const path = generatePath(ROUTES.ResetPassword.path);
            navigate(path);
        },
        goToForgotPassword: () => {
            const path = generatePath(ROUTES.ForgotPassword.path);
            navigate(path);
        },
        getUrlHash() {
            return window.location.hash;
        },
        getHistoryState: () => {
            // History state accessible via useLocation hook can also be accessed as follows
            // Reference: https://github.com/remix-run/react-router/blob/main/packages/router/history.ts#L190
            return window.history?.state?.usr || {};
        },
        goToDeepLinkPage: () => {
            const path = generatePath(ROUTES.Deeplink.path);
            const redirectUri = getUrlHash();
            const queryParams = [
                {
                    name: 'redirectURi',
                    value: redirectUri,
                },
            ];
            const newPath = addClientQueryParamsToPath(path, queryParams);
            navigate(newPath);
        },
        goToWorksheetCreate: (id: string, flags: QueryParams[]) => {
            let path = generatePath('/worksheet/create');
            path = addClientQueryParamsToPath(path, flags);
            navigate(path);
        },
        goToAutoWorksheetWithConnectionId: (
            connectionId?: string,
            origin?: string,
        ) => {
            let path = generatePath('/data/worksheet/create/auto');
            if (connectionId) {
                path = addClientQueryParamsToPath(path, [
                    {
                        name: 'connectionId',
                        value: connectionId,
                    },
                    {
                        name: 'origin',
                        value: origin,
                    },
                ]);
            }
            navigate(path);
        },
        goToFalconCsvUpload: (origin: string, tableId?: string) => {
            let path = generatePath('/data/importcsv');
            const urlQueryParams: QueryParams[] = [
                { name: 'isFalcon', value: 'true' },
            ];
            if (tableId) {
                urlQueryParams.push({
                    name: 'tableId',
                    value: tableId,
                });
            }
            if (origin) {
                urlQueryParams.push({ name: 'origin', value: origin });
            }
            path = addClientQueryParamsToPath(path, urlQueryParams);
            navigate(path);
        },
        goToCsvUploadForEdge: (urlQueryParams?: QueryParams[]) => {
            let path = generatePath('/data/importcsv');
            path = addClientQueryParamsToPath(path, urlQueryParams);
            navigate(path);
        },
        goToGoogleSheetUpload: (flags: QueryParams[]) => {
            let path = generatePath('/data/embrace/connection');
            path = addClientQueryParamsToPath(path, flags);
            navigate(path);
        },
        goToEmbrace: (id: string, flags: QueryParams[]) => {
            let path = generatePath('/data/embrace');
            if (!_.isNil(id)) {
                path = generatePath('/data/embrace/:id', {
                    id,
                });
            }
            path = addClientQueryParamsToPath(path, flags);
            navigate(path);
        },
        goToEmbraceConnections: (flags: QueryParams[]) => {
            let path = generatePath('/data/embrace/connection');
            path = addClientQueryParamsToPath(path, flags);
            navigate(path);
        },
        goToEmbraceConnectionEdit: (connectionId: string) => {
            const path = generatePath('/data/embrace/:connectionId/edit', {
                connectionId,
            });
            navigate(path);
        },
        goToEmbraceCsvUploadWithDatasourceID: (
            tableId: string,
            isAppendOverwriteFlow?: boolean,
        ) => {
            if (!isAppendOverwriteFlow) {
                return;
            }

            const urlQueryParams: QueryParams[] = [
                {
                    name: 'tableId',
                    value: tableId,
                },
            ];
            let path = generatePath('/data/importcsv/loaddata');
            path = addClientQueryParamsToPath(path, urlQueryParams);
            navigate(path);
        },
        goToEmbraceConnectionEditWithFlags: (
            connectionId: string,
            flags: QueryParams[],
        ) => {
            let path = generatePath('/data/embrace/:connectionId/edit', {
                connectionId,
            });
            path = addClientQueryParamsToPath(path, flags);
            navigate(path);
        },
        reload() {
            window.location.reload();
        },
        shouldShowDeepLinkPage() {
            return (
                isPlatformDeeplinkSupported() &&
                !doesUserWantBrowserExperience() &&
                !isDeepLinkingDisabled() &&
                !isEmbeddedApp()
            );
        },
        canShowNavBar(
            isAppEmbedded: boolean,
            primaryNavHidden: boolean,
            isNonEmbedFullAppAccessBlocked: boolean,
        ): boolean {
            if (
                (primaryNavHidden && isAppEmbedded) ||
                (!isAppEmbedded && isNonEmbedFullAppAccessBlocked)
            ) {
                return false;
            }
            return true;
        },
        goToSamlLogin: () => {
            const last401Path = getLast401Path();
            if (!last401Path) {
                window.open('/callosum/v1/saml/login', '_self');
                return;
            }
            const targetURL = encodeURIComponent(getAbsoluteUrl(last401Path));
            window.open(
                `/callosum/v1/saml/login?targetURLPath=${targetURL}`,
                '_self',
            );
        },
        goToOidcLogin: (oidcLoginUri, oidcIssuerURI, urlQueryPathForError) => {
            const last401Path = getLast401Path();
            const targetURL = encodeURIComponent(getAbsoluteUrl(last401Path));
            if (last401Path) {
                window.open(
                    `${oidcLoginUri}?targetURLPath=${targetURL}`,
                    '_self',
                );
            } else if (urlQueryPathForError) {
                try {
                    const url = new URL(oidcIssuerURI);
                    window.open(
                        `${url.origin}/signin/?${urlQueryPathForError}`,
                    );
                } catch (error) {
                    window.open(oidcLoginUri, '_self');
                }
            } else {
                let path = oidcLoginUri;
                if (disableSAMLAutoRedirect()) {
                    path = `${path}?${FLAGS.disableSAMLAutoRedirect.name}=true`;
                }
                if (flags.getValue(FLAGS.disableOIDCAutoRedirect.name)) {
                    path = `${path}?${FLAGS.disableOIDCAutoRedirect.name}=true`;
                }
                if (flags.getValue(FLAGS.forceSAMLAutoRedirect.name)) {
                    if (path.indexOf('?') >= 0) {
                        path = `${path}&${FLAGS.forceSAMLAutoRedirect.name}=true`;
                    } else {
                        path = `${path}?${FLAGS.forceSAMLAutoRedirect.name}=true`;
                    }
                }
                window.open(path, '_self');
            }
        },
        goToPurchase: () => {
            const path = generatePath('/purchase');
            navigate(path);
        },
        // To be implemented
        goToImportTsl: importType => {
            const path = generatePath(CORE_ROUTES.ImportTsl.path, {
                metadataType: importType,
            });
            navigate(path);
        },
        gotoTmlUtility: () => {
            const path = generatePath('/data/utilities/tml');
            navigate(path);
        },
        goToPinboards: _.noop,
        goToSpotApp: _.noop,
        goToSpotAppExport: _.noop,
        goToTables: _.noop,
        goToTslEditor: () => {
            const path = generatePath(CORE_ROUTES.TmlEditorMultiObject.path);
            navigate(path);
        },
        goToTslEditorSingleFile: (metadataType, metadataGuid) => {
            const path = generatePath(CORE_ROUTES.TmlEditor.path, {
                metadataType,
                metadataGuid,
            });
            navigate(path);
        },
        goToUpdateTslFromFile: (metadataType, metadataGuid) => {
            const path = generatePath(CORE_ROUTES.ImportTml.path, {
                metadataType,
                metadataGuid,
            });
            navigate(path);
        },
        toggleUserMenuPopup: _.noop,
        goToSpotAppsAdmin: () => {
            const path = generatePath(routePaths.SpotAppsAdmin);
            navigate(path);
        },
        goToSpotAppDetails: spotAppGuid => {
            const path = `${ROUTES.SpotApp}/${spotAppGuid}`;
            navigate(path);
        },
        goToDbtIntegration: () => {
            const path = generatePath(routePaths.DbtIntegration);
            navigate(path);
        },
        goToSchemaViewer: () => {
            const path = generatePath(routePaths.SchemaViewer);
            navigate(path);
        },
        goToSpotAppsAnalytics: (
            connectionGuid: string,
            spotAppGuid: string,
            isNewApp: boolean,
            reviewStatus: string,
            userid?: string,
        ) => {
            let path;
            if (isNewApp) {
                path = `${ROUTES.SpotApp}/${connectionGuid}/analytics/${spotAppGuid}?isNewApp=true`;

                if (!_.isEmpty(reviewStatus)) {
                    path = `${ROUTES.SpotApp}/${connectionGuid}/analytics/${spotAppGuid}/status/${reviewStatus}?isNewApp=true&userid=${userid}`;
                }
            } else {
                path = `${ROUTES.SpotApp}/${connectionGuid}/analytics/${spotAppGuid}`;

                if (!_.isEmpty(reviewStatus)) {
                    path = `${ROUTES.SpotApp}/${connectionGuid}/analytics/${spotAppGuid}/status/${reviewStatus}?userid=${userid}`;
                }
            }
            navigate(path);
        },
        goToAdmin(landingTab, shouldOpenInNewTab?: boolean) {
            const routingEnabled = isAdminRoutingEnabled();
            // When routing is enabled, the URL is built with the raw tab ID
            // (e.g. /admin/modern-user-management). This is non-canonical;
            // app.tsx's screenID effect will rewrite it to the human-readable
            // slug (e.g. /admin/user-management) via buildAdminUrl() (in the
            // admin MFE's admin-route-utils.ts; the tab ID → slug table lives
            // in admin-route-constants.ts) and cannot be imported here.
            const adminPath =
                routingEnabled && landingTab
                    ? `/admin/${encodeURIComponent(landingTab)}`
                    : '/admin';

            if (isCurrentAdminPath()) {
                triggerAdminTabSwitch(landingTab);
                if (routingEnabled) {
                    navigate(adminPath);
                }
                return;
            }

            if (!routingEnabled) {
                sessionStorage.setItem('AdminLandingTabId', landingTab);
            }

            if (shouldOpenInNewTab) {
                window.open(
                    `${getHost()}/#${generatePath(adminPath)}`,
                    '_blank',
                );
                return;
            }
            navigate(adminPath);
        },
        adminTabSwitcher: {
            registerCb: adminTabObserverSubscriber,
            deregisterCb: unsubscribeAdminTabObserver,
        },
        goToDemo: _.noop,
        getSubDomainIfOrgEnabled: () => {
            return getSubDomainIfOrgEnabled();
        },
        goToDevelopTab: (queryParams?: QueryParams[]) => {
            let path = generatePath('/develop');
            if (queryParams) {
                path = addClientQueryParamsToPath(path, queryParams);
            }
            navigate(path);
        },
        goToWorkSheetV2: (queryParams?: QueryParams[]) => {
            let path = '/worksheet/create';
            if (queryParams) {
                path = addClientQueryParamsToPath(path, queryParams);
            }
            navigate(path);
        },
        goToSetupWithFlags: (queryParams?: QueryParams[]) => {
            let path = '/setup';
            if (queryParams) {
                path = addClientQueryParamsToPath(path, queryParams);
            }
            navigate(path);
        },
        clearClientQueryParams: (paramNames?: string[]) => {
            const { pathname, search } = window.location;
            const [hashPath, queryString] = window.location.hash.split('?');
            if (!paramNames || paramNames.length === 0 || !queryString) {
                window.history.replaceState(
                    null,
                    '',
                    `${pathname}${search}${hashPath}`,
                );
                return;
            }
            const params = new URLSearchParams(queryString);
            paramNames.forEach(name => params.delete(name));
            const remaining = params.toString();
            const newHash = remaining ? `${hashPath}?${remaining}` : hashPath;
            window.history.replaceState(
                null,
                '',
                `${pathname}${search}${newHash}`,
            );
        },
        addClientQueryParamsToCurrentPath: (params: Array<QueryParams>) => {
            const currentHash = window.location.hash.split('?')[0];
            const queryString = params
                .map(p => `${p.name}=${p.value}`)
                .join('&');
            window.history.replaceState(
                null,
                '',
                `${window.location.pathname}${currentHash}?${queryString}`,
            );
        },
        goToGetStarted: () => {
            // If the logged in user has Preview Thoughtspot Sage or Developer
            // privileges, redirect to the get started page, else redirect to the home page.
            let path;
            if (
                hasAdminPrivileges() ||
                hasPreviewThoughtspotSagePrivileges() ||
                hasDeveloperPrivileges()
            ) {
                path = generatePath(routePaths.GetStarted);
            } else {
                path = generatePath(routePaths.Home);
            }
            navigate(path);
        },
        isInitialized: navigate !== emptyNavigate,
        updateHashParams: (
            params: Record<string, string>,
            remove?: boolean,
        ) => {
            // get the current url using window.location.href.
            // it can be in the format of http://localhost:8000?utm_source=kpi_monitor&utm_medium=email#/insights/conv-assist or http://localhost:8000?utm_source=kpi_monitor&utm_medium=email#/insights/conv-assist?queryParam1=true&queryParam2=true
            // hash should be like #/insights/conv-assist or #/insights/conv-assist?queryParam1=true&queryParam2=true
            // the function should iterate over the params(ex: {param1: 'param1Value', param2: 'param2Value'}) and update the hash with the new params
            // if the param is not in the hash, add it to the hash
            // if the param is in the hash, update it with the new value
            // if the param is not in the hash and remove is true, do nothing
            // if the param is in the hash and remove is true, remove it from the hash
            // return the new url in the format of http://localhost:8000?utm_source=kpi_monitor&utm_medium=email#/insights/conv-assist?queryParam1=true&queryParam2=true

            const url = window.location.href;

            // Split URL into base (everything before #) and hash (# and everything after)
            const hashIndex = url.indexOf('#');
            const baseUrl =
                hashIndex !== -1 ? url.substring(0, hashIndex) : url;
            const hashPart =
                hashIndex !== -1 ? url.substring(hashIndex + 1) : '';

            // Parse hash into path and query string
            // Format: /insights/conv-assist or /insights/conv-assist?queryParam1=true&queryParam2=true
            const [hashPath, hashQueryString = ''] = hashPart.split('?');

            // Parse existing hash parameters into an object
            const hashParams = new URLSearchParams(hashQueryString);

            // Update hash parameters based on the remove flag
            Object.entries(params).forEach(([key, value]) => {
                if (remove) {
                    // Remove parameter if it exists
                    hashParams.delete(key);
                } else {
                    // Add or update parameter
                    hashParams.set(key, String(value));
                }
            });

            // Reconstruct the hash
            const newHashQueryString = hashParams.toString();
            const newHash = newHashQueryString
                ? `#${hashPath}?${newHashQueryString}`
                : `#${hashPath}`;

            // Combine base URL with new hash
            const newUrl = baseUrl + newHash;

            return newUrl;
        },
    };
}

const useCustomNavigateFunction = () => {
    const routerNavigate = useRouterNavigate();
    const matches = useMatches();
    return (to: To | number, options: NavigateOptions = {}) => {
        if (typeof to === 'number') {
            return routerNavigate(to);
        }
        const targetPath = typeof to === 'string' ? to : to.pathname;
        const overrideOptions = shouldOverrideHistoryStateInEmbed()
            ? { replace: true }
            : {};
        const resolvedOptions = { ...options, ...overrideOptions };
        const currentAppMatch = matches.find(
            match => (match.handle as any)?.handle,
        );
        const currentApp =
            currentAppMatch &&
            currentAppMatch.id !== ApplicationList[Applications.CORE].id &&
            ApplicationList[currentAppMatch.id as keyof typeof ApplicationList];
        if (
            !currentApp ||
            currentApp.appName === Applications.CORE ||
            Object.values(currentApp.children).some(route => {
                const pathMatch = matchPath(
                    (route as PrivilegedRouteObject)?.path || '',
                    targetPath,
                );
                return (
                    pathMatch &&
                    !ApplicationList[
                        pathMatch.params['*'] as keyof typeof ApplicationList
                    ]
                );
            })
        ) {
            return routerNavigate(to, resolvedOptions);
        }
        if (!isRedirectToV1Disabled() && !v1Disabled()) {
            // eslint-disable-next-line security/detect-non-literal-fs-filename
            return window.open(`#${targetPath}`, '_self');
        }
        return routerNavigate(targetPath, resolvedOptions);
    };
};

export function useSetupNavService() {
    const [promptProps, setPromptProps] = useState<{
        message: string;
        when: boolean;
    }>({
        message: '',
        when: false,
    });
    usePrompt(promptProps);
    const { setNavService, sessionService } = useAppContext();
    const navigate = useCustomNavigateFunction();
    return () => {
        const navService = getNavService(
            navigate,
            setPromptProps,
            sessionService.getLast401Path,
        );
        sessionService.getOnUserLoggedOut().subscribe(() => {
            navService.goToLogin();
        });
        setNavService(navService);
    };
}

export function getUrlPath(): string {
    return window.location.pathname;
}

export function getCurrentPage() {
    // TODO
}

export function getPreviousPage() {
    // TODO
}

export function reloadApplication() {
    window.location.reload();
}

export function getCurrentPathByLocationHash(
    includeQueryParams = true,
): string {
    let currentPath = window.location.hash;
    if (currentPath.startsWith('#')) {
        currentPath = currentPath.slice(1);
    }
    if (includeQueryParams) return currentPath;
    return currentPath.split('?')[0];
}

export function removeOverrideOrgId(url: string): string {
    let currentUrl = url;
    if (currentUrl.includes(`${FLAGS.overrideOrgId.name}=`)) {
        const [baseUrl, hash] = currentUrl.split('#');
        const [basePath, queryString] = baseUrl.split('?');

        const queryStringWithoutOrgOverride = queryString.replace(
            /(?:&|^)orgId=[^&]*/,
            '',
        );

        currentUrl = `${basePath}${
            queryStringWithoutOrgOverride
                ? `?${queryStringWithoutOrgOverride}`
                : ''
        }${hash ? `#${hash}` : ''}`;
        return currentUrl;
    }
    return currentUrl;
}

const addOrgNameToHost = (url: string, targetOrgName?: string) => {
    const orgName = targetOrgName ?? getCurrentOrgName();
    if (!orgName || orgName === jsonConstants.DEFAULT_ORG_NAME) {
        return url;
    }
    const urlObj = new URL(url);
    const host = urlObj.hostname;
    if (host.startsWith(`${orgName}.`)) {
        return url;
    }
    const newHost = `${orgName}.${host}`;
    urlObj.hostname = newHost;
    return urlObj.toString();
};

const addOrgIdParam = (url: string, targetOrgId?: number) => {
    const splittedUrl = url.split(QUERY_PARAM_DELIMITER);
    let orgId = targetOrgId ?? getCurrentOrgId();
    const overrideOrgId = flags.getValue(FLAGS.overrideOrgId.name);
    if (overrideOrgId === 0 || overrideOrgId) {
        orgId = overrideOrgId;
    }

    if (url.includes(`${FLAGS.overrideOrgId.name}=`)) {
        splittedUrl[0] = url.replace(
            new RegExp(`${FLAGS.overrideOrgId.name}=[^&]*`),
            `${FLAGS.overrideOrgId.name}=${orgId}`,
        );
    } else {
        const orgIdParam = `${FLAGS.overrideOrgId.name}=${orgId}`;
        splittedUrl[0] += splittedUrl[0].includes('?')
            ? `&${orgIdParam}`
            : `?${orgIdParam}`;
    }

    return splittedUrl.join(QUERY_PARAM_DELIMITER);
};

export function addOrgQueryParamOrSubdomain(
    url: string,
    targetOrg?: { orgId?: number; orgName?: string },
): string {
    if (
        !isOrgUiEnabled() ||
        (!isPerOrgSubDomainEnabled() && !isOrgPerUrlEnabled())
    ) {
        return url;
    }

    if (isPerOrgSubDomainEnabled()) {
        return addOrgNameToHost(url, targetOrg?.orgName);
    }

    if (url.includes('/admin')) {
        return removeOverrideOrgId(url);
    }

    return addOrgIdParam(url, targetOrg?.orgId);
}

export function isCurrentPrintViewPath() {
    const pathname = window.location.hash;
    return (
        pathname.includes(ROUTES.PrintView.path) ||
        pathname.includes(ROUTES.PrintViewV2.path)
    );
}

export const getUrlWithoutUTMParameters = () => {
    const fullUrl = window.location.href;
    const [baseUrl, hash] = fullUrl?.split('#');
    const url = new URL(baseUrl);
    const urlParams = new URLSearchParams(url.search);
    urlParams.delete(FLAGS.utm_source.name);
    urlParams.delete(FLAGS.utm_medium.name);
    url.search = urlParams?.toString();
    const newUrl = url?.toString() + (hash ? `#${hash}` : '');
    return newUrl;
};

export const replaceState = (url: string) => {
    window.history.replaceState(null, '', url);
};
